import type { OrganizationUser } from '@compass/client/types/organization_user';
import { Loader2 } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRemoveUserFromOrganization } from '@/hooks/useOrganizationMembers';

interface RemoveUserDialogProps {
  organizationId: string;
  user: OrganizationUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RemoveUserDialog: React.FC<RemoveUserDialogProps> = ({
  organizationId,
  user,
  open,
  onOpenChange,
}) => {
  const removeMutation = useRemoveUserFromOrganization();

  const handleRemove = async () => {
    if (!user?.user_id) return;

    try {
      await removeMutation.mutateAsync({
        organization_id: organizationId,
        user_id: user.user_id,
      });

      toast.success(`${user.name || user.email} has been removed from the organization`);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to remove user from organization',
      );
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Remove User</DialogTitle>
          <DialogDescription className="pt-4">
            Are you sure you want to remove{' '}
            <span className="font-medium">{user.name || user.email}</span> from this organization?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <p className="text-sm font-medium">This will:</p>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            <li>Remove the user from the organization</li>
            <li>Remove all their roles and program access</li>
            <li>This action cannot be undone</li>
          </ul>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={removeMutation.isPending}
          >
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleRemove} disabled={removeMutation.isPending}>
            {removeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Remove User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
